// src/pages/settings/reportTemplates/ReportBuilderDrawer.tsx

import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Space, message, Typography } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { setNewReportTemplate, updateReportTemplate } from "../../../../api/healthcare/ReportApi";
import { ReportTemplate } from "../../../../types/healthcare/ReportTypes";
import DetailDrawer from "../../../../components/shared/DetailDrawer";

const { Text } = Typography;

interface ReportBuilderDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	template: ReportTemplate;
	isSubmitting: boolean;
	setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

export const userAttributes = [
	{ label: "Address", value: "address" },
	{ label: "City", value: "city" },
	{ label: "Company", value: "company" },
	{ label: "Country", value: "country" },
	{ label: "Date of Birth", value: "date_of_birth" },
	{ label: "Email", value: "email" },
	{ label: "First Name", value: "first_name" },
	{ label: "Form Link", value: "form_link" },
	{ label: "Gender", value: "gender" },
	{ label: "Last Name", value: "last_name" },
	{ label: "Middle Name", value: "middle_name" },
	{ label: "Password", value: "password" },
	{ label: "Phone", value: "phone" },
	{ label: "Postal Code", value: "postal_code" },
	{ label: "Profile Picture", value: "profile_picture" },
	{ label: "Sex", value: "sex" },
	{ label: "State", value: "state" },
	{ label: "Username", value: "username" },
	{ label: "Booking Start", value: "booking_start" },
	{ label: "Booking End", value: "booking_end" },
	{ label: "Consultation Link", value: "consultation_link" },
];

// ── Hoverable attribute list item ────────────────────────────
const AttributeItem: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => {
	const [hovered, setHovered] = useState(false);
	return (
		<div
			style={{
				padding: "6px 16px",
				cursor: "pointer",
				fontSize: 12,
				background: hovered ? "var(--mg-primary-bg)" : "transparent",
				color: hovered ? "var(--mg-primary)" : "var(--mg-text)",
				borderBottom: "1px solid var(--mg-border)",
				transition: "background 0.15s",
			}}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			onMouseDown={(e) => e.preventDefault()}
			onClick={onClick}
		>
			{label}
		</div>
	);
};

// ── Main component ────────────────────────────────────────────
const ReportBuilderDrawer: React.FC<ReportBuilderDrawerProps> = ({ isOpen, onClose, template, isSubmitting, setIsSubmitting }) => {
	const [updatedTemplate, setUpdatedTemplate] = useState<ReportTemplate>(template);
	const [editorContent, setEditorContent] = useState<string>("");
	const [editorKey, setEditorKey] = useState<number>(0);
	const [isEditorReady, setIsEditorReady] = useState<boolean>(false);
	const quillRef = useRef<ReactQuill>(null);

	// Reset editor when drawer opens or template changes
	useEffect(() => {
		if (isOpen) {
			setUpdatedTemplate(template);
			setIsEditorReady(false);
			setEditorKey((prev) => prev + 1);
			setTimeout(() => {
				setEditorContent(template.body || "");
				setIsEditorReady(true);
			}, 50);
		} else {
			setEditorContent("");
			setIsEditorReady(false);
		}
	}, [isOpen, template]);

	const handleSave = async () => {
		setIsSubmitting(true);
		try {
			const templateToSave = { ...updatedTemplate, body: editorContent };
			if (templateToSave.id === 0) {
				await setNewReportTemplate(templateToSave);
				message.success("Report template created successfully");
			} else {
				await updateReportTemplate(templateToSave);
				message.success("Report template updated successfully");
			}
			onClose();
		} catch (error: unknown) {
			console.error(error);
			message.error("Error saving template");
		}
		setIsSubmitting(false);
	};

	const handleInputChange = (field: string, value: string) => {
		setUpdatedTemplate((prev) => ({ ...prev, [field]: value }));
	};

	const handleInsertAttribute = (attribute: string) => {
		if (quillRef.current) {
			const quill = quillRef.current.getEditor();
			const range = quill.getSelection();
			const position = range ? range.index : quill.getLength();
			const token = `{{${attribute}}}`;
			quill.insertText(position, token);
			quill.setSelection(position + token.length);
		}
	};

	const modules = {
		toolbar: [
			[{ header: [1, 2, 3, false] }],
			["bold", "italic", "underline", "strike"],
			[{ list: "ordered" }, { list: "bullet" }],
			[{ color: [] }, { background: [] }],
			["link", "image"],
			["clean"],
		],
	};

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			width="85vw"
			title={updatedTemplate.id === 0 ? "New Report Template" : updatedTemplate.name || "Edit Report Template"}
			subtitle={updatedTemplate.id !== 0 ? "Report Template" : undefined}
			headerActions={
				<Button type="primary" icon={<SaveOutlined />} loading={isSubmitting} onClick={handleSave}>
					Save
				</Button>
			}
			noPadding
		>
			<div style={{ display: "flex", height: "100%" }}>
				{/* Sidebar — user attributes */}
				<div
					style={{
						width: 240,
						borderRight: "1px solid var(--mg-border)",
						overflowY: "auto",
						flexShrink: 0,
					}}
				>
					<div style={{ padding: "12px 16px", fontSize: 12, fontWeight: 600, color: "var(--mg-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--mg-border)" }}>
						User Attributes
					</div>
					{userAttributes.map((attr) => (
						<AttributeItem
							key={attr.value}
							label={attr.label}
							onClick={() => handleInsertAttribute(attr.value)}
						/>
					))}
				</div>

				{/* Main content */}
				<div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
					<Space direction="vertical" size={16} style={{ width: "100%" }}>
						<div>
							<Text style={{ display: "block", marginBottom: 6, fontSize: 12, color: "var(--mg-text-secondary)" }}>Name</Text>
							<Input
								placeholder="Enter report name"
								value={updatedTemplate.name}
								onChange={(e) => handleInputChange("name", e.target.value)}
							/>
						</div>

						<div>
							<Text style={{ display: "block", marginBottom: 6, fontSize: 12, color: "var(--mg-text-secondary)" }}>Description</Text>
							<Input
								placeholder="Enter description"
								value={updatedTemplate.description || ""}
								onChange={(e) => handleInputChange("description", e.target.value)}
							/>
						</div>

						<div>
							<Text style={{ display: "block", marginBottom: 6, fontSize: 12, color: "var(--mg-text-secondary)" }}>Body</Text>
							{isOpen && (
								<ReactQuill
									key={editorKey}
									ref={quillRef}
									theme="snow"
									value={editorContent}
									onChange={setEditorContent}
									style={{ height: 450 }}
									modules={modules}
								/>
							)}
						</div>
					</Space>
				</div>
			</div>
		</DetailDrawer>
	);
};

export default ReportBuilderDrawer;

